import React, { useEffect, useState } from 'react';
import {
  Card,
  Select,
  Form,
  Typography,
  Divider,
  Spin,
  Alert,
  Input,
  Button,
  Checkbox,
  message,
} from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { apiService } from '../../services/api';
import { Region, ProductAllowedRegion, ProvisioningQuestion } from '../../types';
import { UpdateOperation, EntityType } from './types';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

export interface RegionsQuestionsData {
  allowedRegions: string[];
  questions: ProvisioningQuestionFormEntry[];
}

export interface ProvisioningQuestionFormEntry {
  /** undefined means it is a newly added question (not yet persisted) */
  id?: number;
  name: string;
  display_name: string;
  description: string;
  type: 'text' | 'select' | 'multiselect' | 'boolean';
  /** 'existing' | 'new' | 'deleted' — used to decide update vs insert vs delete */
  _status: 'existing' | 'new' | 'deleted';
}

interface Props {
  productId: number;
  productName: string;
  onUpdate: (update: UpdateOperation) => void;
  existingUpdates: UpdateOperation[];
}

const RegionsQuestionsEditor: React.FC<Props> = ({
  productId,
  productName,
  onUpdate,
  existingUpdates,
}) => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Resolved initial values
  const [initialRegions, setInitialRegions] = useState<string[]>([]);
  const [initialQuestions, setInitialQuestions] = useState<ProvisioningQuestionFormEntry[]>([]);

  // Form state
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [questions, setQuestions] = useState<ProvisioningQuestionFormEntry[]>([]);
  const [hasProvisioningQuestions, setHasProvisioningQuestions] = useState(false);
  const [questionErrors, setQuestionErrors] = useState<Record<number, Record<string, string>>>({});

  const validateQuestionField = (index: number, field: string, value: string) => {
    const msg =
      value.startsWith(' ') || value.endsWith(' ')
        ? 'Must not start or end with a space'
        : '';
    setQuestionErrors((prev) => ({
      ...prev,
      [index]: { ...prev[index], [field]: msg },
    }));
  };

  // ── Load data ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [allRegions, productRegions, productQuestions] = await Promise.all([
          apiService.getRegions(),
          apiService.getProductAllowedRegions(productId),
          apiService.getProvisioningQuestions(productId),
        ]);

        setRegions(allRegions);

        // Check if there is already a pending update saved in the wizard state
        const pendingUpdate = existingUpdates.find(
          (u) => u.entityType === EntityType.PRODUCT && u.entityId === String(productId) &&
                 'regionsQuestions' in u.updates
        );

        if (pendingUpdate) {
          const saved = pendingUpdate.updates.regionsQuestions as RegionsQuestionsData;
          setSelectedRegions(saved.allowedRegions);
          setQuestions(saved.questions);
          setHasProvisioningQuestions(saved.questions.filter(q => q._status !== 'deleted').length > 0);
        } else {
          const regionCodes = productRegions.map((r: ProductAllowedRegion) => r.region_name);
          const mappedQuestions: ProvisioningQuestionFormEntry[] = productQuestions.map(
            (q: ProvisioningQuestion) => ({
              id: q.id,
              name: q.name,
              display_name: q.display_name || '',
              description: q.description || '',
              type: q.type as ProvisioningQuestionFormEntry['type'],
              _status: 'existing' as const,
            })
          );

          setSelectedRegions(regionCodes);
          setInitialRegions(regionCodes);
          setQuestions(mappedQuestions);
          setInitialQuestions(mappedQuestions);
          setHasProvisioningQuestions(mappedQuestions.length > 0);
        }
      } catch (err) {
        setError('Failed to load regions and provisioning questions.');
      } finally {
        setLoading(false);
      }
    };

    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // ── Emit update whenever values change ────────────────────────────────────
  useEffect(() => {
    if (loading) { return; }

    const data: RegionsQuestionsData = {
      allowedRegions: selectedRegions,
      questions,
    };

    const update: UpdateOperation = {
      entityType: EntityType.PRODUCT,
      entityId: String(productId),
      entityName: productName,
      updates: { regionsQuestions: data },
      originalData: {
        regionsQuestions: {
          allowedRegions: initialRegions,
          questions: initialQuestions,
        },
      },
    };

    onUpdate(update);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegions, questions, loading]);

  // ── Region handlers ────────────────────────────────────────────────────────
  const handleRegionChange = (values: string[]) => {
    if (values.includes('GLOBAL') && values.length > 1) {
      const wasGlobalSelected = selectedRegions.includes('GLOBAL');
      if (!wasGlobalSelected) {
        setSelectedRegions(['GLOBAL']);
        message.warning('GLOBAL region selected. All other region selections have been cleared.');
        return;
      } else {
        setSelectedRegions(['GLOBAL']);
        message.warning('Cannot select other regions when GLOBAL is already selected.');
        return;
      }
    } else {
      setSelectedRegions(values);
    }
  };

  const isGlobalSelected = selectedRegions.includes('GLOBAL');

  // ── Question handlers ──────────────────────────────────────────────────────
  const addQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      { name: '', display_name: '', description: '', type: 'text', _status: 'new' },
    ]);
    setHasProvisioningQuestions(true);
  };

  const removeQuestion = (index: number) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== index) { return q; }
        // Existing questions are soft-deleted; new ones are spliced out
        return q._status === 'new' ? null : { ...q, _status: 'deleted' as const };
      }).filter(Boolean) as ProvisioningQuestionFormEntry[]
    );
  };

  const updateQuestion = (
    index: number,
    field: keyof Omit<ProvisioningQuestionFormEntry, '_status' | 'id'>,
    value: string
  ) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== index) { return q; }
        return { ...q, [field]: value };
      })
    );
  };

  const activeQuestions = questions.filter((q) => q._status !== 'deleted');

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
        <div style={{ marginTop: 12 }}>
          <Text type="secondary">Loading regions and questions…</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return <Alert type="error" message={error} showIcon />;
  }

  return (
    <div>
      <Title level={4} style={{ marginBottom: 4 }}>
        Regions &amp; Provisioning Questions
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
        Editing <strong>{productName}</strong> — existing values are pre-filled. Changes will be applied on submit.
      </Text>

      {/* ── Allowed Regions ── */}
      <Card title="Allowed Regions" style={{ marginBottom: 16 }}>
        {isGlobalSelected && (
          <Alert
            message="GLOBAL Region Selected"
            description="No other region can be selected when GLOBAL is active. Remove GLOBAL first to select specific regions."
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            closable
          />
        )}
        <Form.Item
          label="Select Allowed Regions"
          required
          style={{ marginBottom: 0 }}
        >
          <Select
            mode="multiple"
            placeholder="Select regions"
            showSearch
            value={selectedRegions}
            onChange={handleRegionChange}
            filterOption={(input, option) =>
              (option?.children?.toString() || '').toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: '100%' }}
          >
            {regions.map((region) => (
              <Option key={region.region_code} value={region.region_code}>
                {region.region_name} ({region.region_code})
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Card>

      {/* ── Provisioning Questions ── */}
      <Card
        title="Provisioning Questions (Optional)"
        extra={
          <Checkbox
            checked={hasProvisioningQuestions}
            onChange={(e) => {
              setHasProvisioningQuestions(e.target.checked);
              if (!e.target.checked) {
                // Soft-delete all existing; remove all new ones
                setQuestions((prev) =>
                  prev
                    .map((q) =>
                      q._status === 'new' ? null : { ...q, _status: 'deleted' as const }
                    )
                    .filter(Boolean) as ProvisioningQuestionFormEntry[]
                );
              }
            }}
          >
            Has Provisioning Questions
          </Checkbox>
        }
      >
        {hasProvisioningQuestions && (
          <>
            {activeQuestions.length === 0 && (
              <Text type="secondary">No questions yet. Click below to add one.</Text>
            )}

            {questions.map((q, index) => {
              if (q._status === 'deleted') { return null; }
              return (
                <Card
                  key={index}
                  size="small"
                  style={{ marginBottom: 8 }}
                  title={
                    <span>
                      Question {index + 1}
                      {q._status === 'new' && (
                        <Text type="secondary" style={{ marginLeft: 8, fontWeight: 'normal', fontSize: 12 }}>
                          (new)
                        </Text>
                      )}
                    </span>
                  }
                  extra={
                    <MinusCircleOutlined
                      style={{ color: '#ff4d4f', cursor: 'pointer' }}
                      onClick={() => removeQuestion(index)}
                    />
                  }
                >
                  <Form.Item
                    label="Question Name (Internal)"
                    required
                    style={{ marginBottom: 8 }}
                    validateStatus={questionErrors[index]?.name ? 'error' : ''}
                    help={questionErrors[index]?.name}
                  >
                    <Input
                      placeholder="e.g., tenant_region"
                      value={q.name}
                      onChange={(e) => updateQuestion(index, 'name', e.target.value)}
                      onBlur={(e) => {
                        updateQuestion(index, 'name', e.target.value.trim());
                        validateQuestionField(index, 'name', e.target.value);
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Display Name"
                    required
                    style={{ marginBottom: 8 }}
                    validateStatus={questionErrors[index]?.display_name ? 'error' : ''}
                    help={questionErrors[index]?.display_name}
                  >
                    <Input
                      placeholder="e.g., Select Tenant Region"
                      value={q.display_name}
                      onChange={(e) => updateQuestion(index, 'display_name', e.target.value)}
                      onBlur={(e) => {
                        updateQuestion(index, 'display_name', e.target.value.trim());
                        validateQuestionField(index, 'display_name', e.target.value);
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Description"
                    style={{ marginBottom: 8 }}
                    validateStatus={questionErrors[index]?.description ? 'error' : ''}
                    help={questionErrors[index]?.description}
                  >
                    <TextArea
                      rows={2}
                      placeholder="Question description"
                      value={q.description}
                      onChange={(e) => updateQuestion(index, 'description', e.target.value)}
                      onBlur={(e) => {
                        updateQuestion(index, 'description', e.target.value.trim());
                        validateQuestionField(index, 'description', e.target.value);
                      }}
                    />
                  </Form.Item>
                  <Form.Item label="Question Type" required style={{ marginBottom: 0 }}>
                    <Select
                      value={q.type}
                      onChange={(val) => updateQuestion(index, 'type', val)}
                      style={{ width: '100%' }}
                    >
                      <Option value="text">Text</Option>
                      <Option value="select">Select</Option>
                      <Option value="multiselect">Multi-Select</Option>
                      <Option value="boolean">Boolean</Option>
                    </Select>
                  </Form.Item>
                </Card>
              );
            })}

            <Divider style={{ margin: '12px 0' }} />
            <Button type="dashed" onClick={addQuestion} block icon={<PlusOutlined />}>
              Add Provisioning Question
            </Button>
          </>
        )}

        {!hasProvisioningQuestions && (
          <Text type="secondary">
            Check the box above to add or manage provisioning questions.
          </Text>
        )}
      </Card>
    </div>
  );
};

export default RegionsQuestionsEditor;
